import * as Tone from 'tone';
import { createEventHook } from '@vueuse/core';
import { App } from '@capacitor/app';
import { withFallbackTimeout } from '@/utils';

let audioContext: AudioContext | null = null;
const audioContextRecreated = createEventHook<AudioContext>();
let silentUnlockAudio: HTMLAudioElement | null = null;
const START_TONE_TIMEOUT_MS = 1000;

App.addListener('appStateChange', ({ isActive }) => {
  if (!isActive && audioContext && audioContext.state === 'running') {
    audioContext.suspend();
  }
});

const createContext = (): AudioContext => {
  if (!window.AudioContext) {
    throw new Error('AudioContext is not available in this environment.');
  }
  audioContext = new window.AudioContext();
  Tone.setContext(audioContext);
  audioContext.onstatechange = async () => {
    if (!audioContext) return;
    const state: string = audioContext.state;
    if (state === 'interrupted') {
      await audioContext.resume();
    }
  };
  audioContextRecreated.trigger(audioContext);
  return audioContext;
};

const getAudioContext = (): AudioContext => {
  if (!audioContext) {
    return createContext();
  }
  return audioContext;
};

const isAudioRunning = (): boolean => audioContext?.state === 'running';

const getSilentUnlockAudio = (): HTMLAudioElement | null => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  if (silentUnlockAudio) return silentUnlockAudio;
  const audio = new Audio('/silence 10s.mp3');
  audio.loop = true;
  audio.autoplay = false;
  audio.controls = false;
  audio.preload = 'auto';
  audio.setAttribute('playsinline', '');
  audio.setAttribute('webkit-playsinline', 'true');
  audio.style.display = 'none';
  document.body.appendChild(audio);
  silentUnlockAudio = audio;
  return audio;
};

const primeSilentAudio = async () => {
  const audio = getSilentUnlockAudio();
  if (!audio || !audio.paused) return;
  await audio.play();
};

const ensureReadyFromUserGesture = async (): Promise<AudioContext> => {
  const ctx = getAudioContext();
  const wait = (operation: Promise<void>, label: string) =>
    withFallbackTimeout<void>(
      operation.catch(error => {
        console.error(`${label} failed:`, error);
        throw error;
      }),
      () => {
        location.reload();
        throw new Error(`${label} failed or timed out.`);
      },
      START_TONE_TIMEOUT_MS,
      label,
    );

  await wait(ctx.suspend(), 'ctx.suspend()');
  await wait(primeSilentAudio(), 'Silent audio unlock');
  await wait(Tone.start(), 'Tone.start()');

  const startTime = ctx.currentTime;
  const source = ctx.createBufferSource();
  let started = false;
  try {
    await wait(new Promise<void>((resolve, reject) => {
      source.buffer = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * 0.1), ctx.sampleRate);
      source.connect(ctx.destination);
      source.onended = () => {
        if (ctx.state === 'running' && ctx.currentTime - startTime >= 0.05) {
          resolve();
        } else {
          reject(new Error('Audio context is not running or its clock is stalled.'));
        }
      };
      source.start();
      started = true;
    }), 'Silent playback check');
    return ctx;
  } finally {
    source.onended = null;
    if (started) source.stop();
    source.disconnect();
  }
};

const onAudioContextRecreated = audioContextRecreated.on;

export function useGlobalAudioContext() {
  return {
    getAudioContext,
    isAudioRunning,
    ensureReadyFromUserGesture,
    onAudioContextRecreated,
  };
}
